"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { m, AnimatePresence, type Variants } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import { cdnUrl } from "@/lib/cdn";
import { useTrialForm } from "@/components/providers/TrialFormProvider";

const ease = [0.76, 0, 0.24, 1] as const;

const productLinks = [
  { label: "The Co-Analyst", href: "/products#co-analyst" },
  { label: "AI-Powered Search", href: "/products#market-intelligence" },
  { label: "Forensic Risk Assessment", href: "/products#forensic" },
  { label: "Agents", href: "/products#agents" },
  { label: "Research Library", href: "/products#research-library" },
  { label: "Coverage & MCP", href: "/products#coverage" },
];

const solutionsLinks = [
  { label: "Institutional Investing", href: "/institutional-investing" },
  { label: "Corporate Strategy", href: "/corporate-strategy" },
  { label: "Securities Law", href: "/securities-law" },
  { label: "Brokerage & Banking", href: "/brokerage-banking" },
  { label: "Public D&O Insurance", href: "/insurance" },
  { label: "Investor Relations", href: "/investor-relations" },
];

const companyLinks = [
  { label: "About", href: "/about" },
  { label: "Technology", href: "/technology" },
  { label: "In the Media", href: "/in-the-media" },
];

const blogLinks = [
  { label: "Company Updates", href: "/blog" },
  { label: "Co-Analyst Research", href: "/research" },
];

const menuSlide: Variants = {
  initial: { x: "100%" },
  enter: {
    x: "0%",
    transition: { duration: 0.8, ease },
  },
  exit: {
    x: "100%",
    transition: { duration: 0.6, ease },
  },
};

const linkSlide: Variants = {
  initial: { x: 80, opacity: 0 },
  enter: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: { duration: 0.8, ease, delay: 0.05 * i },
  }),
  exit: (i: number) => ({
    x: 80,
    opacity: 0,
    transition: { duration: 0.4, ease, delay: 0.03 * i },
  }),
};

function DropdownMenu({
  label,
  href,
  links,
  scrolled,
}: {
  label: string;
  href: string;
  links: { label: string; href: string }[];
  scrolled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  const enter = () => {
    clearTimeout(timeout.current);
    setOpen(true);
  };

  const leave = () => {
    timeout.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <Link href={href} className={`relative text-[13px] px-4 py-1.5 rounded-full transition-all duration-300 inline-flex items-center gap-1 ${scrolled ? "text-neutral-600 hover:text-black hover:bg-white/80" : "text-white/70 hover:text-white hover:bg-white/10"}`}>
        {label}
        <svg
          className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </Link>

      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute top-full left-0 mt-2 w-56 py-2 bg-white rounded-lg border border-black/[0.06] shadow-[0_8px_30px_rgba(0,0,0,0.08)] z-50"
          >
            {links.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="block px-4 py-2.5 text-[13px] text-neutral-600 hover:text-black hover:bg-black/[0.02] transition-colors duration-200"
                onClick={() => setOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { openTrialForm } = useTrialForm();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);

  // The homepage hero is now a light field like the rest of the site, so the
  // header always uses dark text/logo (no more white-on-dark hero variant).
  const isDarkHero = false;
  const darkText = !isDarkHero || scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMobileSection(null);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
    setMobileSection(null);
  }, []);

  const toggleSection = useCallback((title: string) => {
    setMobileSection((prev) => (prev === title ? null : title));
  }, []);

  const closeAndTrial = useCallback(() => {
    setMenuOpen(false);
    openTrialForm();
  }, [openTrialForm]);

  const mobileNavSections = [
    { title: "Product", href: "/products", links: productLinks },
    { title: "Solutions", href: "/institutional-investing", links: solutionsLinks },
    { title: "Pricing", href: "/pricing", links: [] as { label: string; href: string }[] },
    { title: "Company", href: "/about", links: companyLinks },
    { title: "Blog", href: "/blog", links: blogLinks },
  ];

  return (
    <>
      <header
        data-og-hide
        className={`fixed top-[40px] left-0 right-0 z-50 transition-all duration-300 ${
          menuOpen ? "lg:opacity-100 opacity-0 pointer-events-none lg:pointer-events-auto" : "opacity-100"
        } ${scrolled ? "py-2" : "py-4"}`}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8">
          <div
            className={`flex items-center justify-between rounded-2xl px-6 h-14 transition-all duration-300 ${
              scrolled
                ? "bg-white/95 backdrop-blur-md border border-black/[0.06] shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
                : "bg-transparent border border-transparent shadow-none"
            }`}
          >
            {/* Logo */}
            <Link href="/" className="flex items-center group relative z-[60]">
              <Image
                src={cdnUrl("/images/hudson-labs-logo.svg")}
                alt="Hudson Labs"
                width={691}
                height={143}
                sizes="140px"
                className={`h-7 w-auto transition-all duration-300 ${!darkText ? "brightness-0 invert" : ""}`}
                loading="eager"
                unoptimized
              />
            </Link>

            {/* Desktop Nav */}
            <nav className={`hidden lg:flex items-center gap-1 rounded-full px-1.5 py-1 transition-colors duration-300 ${darkText ? "bg-black/[0.03]" : "bg-white/[0.08]"}`}>
              <DropdownMenu label="Product" href="/products" links={productLinks} scrolled={darkText} />
              <DropdownMenu label="Solutions" href="/institutional-investing" links={solutionsLinks} scrolled={darkText} />
              <Link
                href="/pricing"
                className={`relative text-[13px] px-4 py-1.5 rounded-full transition-all duration-300 ${darkText ? "text-neutral-600 hover:text-black hover:bg-white/80" : "text-white/70 hover:text-white hover:bg-white/10"}`}
              >
                Pricing
              </Link>
              <DropdownMenu label="Company" href="/about" links={companyLinks} scrolled={darkText} />
              <DropdownMenu label="Blog" href="/blog" links={blogLinks} scrolled={darkText} />
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="https://platform.hudson-labs.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={`text-[13px] transition-colors duration-300 px-3 py-1.5 ${darkText ? "text-neutral-500 hover:text-black" : "text-white/50 hover:text-white"}`}
              >
                Sign In
              </a>
              <m.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <button
                  onClick={openTrialForm}
                  className="relative inline-flex items-center px-5 py-2 text-[13px] font-medium text-white rounded-md overflow-hidden group"
                >
                  <span className="absolute inset-0 bg-black group-hover:bg-neutral-800 transition-colors duration-300" />
                  <span className="relative">Start Free Trial</span>
                </button>
              </m.div>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={toggleMenu}
              className="lg:hidden w-10 h-10 flex items-center justify-center relative z-[60]"
              aria-label="Toggle menu"
            >
              <div className="w-6 flex flex-col items-end gap-[5px]">
                <m.span
                  animate={
                    menuOpen
                      ? { rotate: 45, y: 7, width: "100%", backgroundColor: "#fff" }
                      : { rotate: 0, y: 0, width: "100%", backgroundColor: darkText ? "#000" : "#fff" }
                  }
                  transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  className="block h-[1.5px] origin-center rounded-full"
                />
                <m.span
                  animate={
                    menuOpen
                      ? { opacity: 0, width: "0%" }
                      : { opacity: 1, width: "60%" }
                  }
                  transition={{ duration: 0.3 }}
                  className={`block h-[1.5px] rounded-full transition-colors duration-300 ${darkText ? "bg-black" : "bg-white"}`}
                />
                <m.span
                  animate={
                    menuOpen
                      ? { rotate: -45, y: -7, width: "100%", backgroundColor: "#fff" }
                      : { rotate: 0, y: 0, width: "80%", backgroundColor: darkText ? "#000" : "#fff" }
                  }
                  transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  className="block h-[1.5px] origin-center rounded-full"
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Fullscreen Mobile Menu */}
      <AnimatePresence mode="wait">
        {menuOpen && (
          <m.div
            variants={menuSlide}
            initial="initial"
            animate="enter"
            exit="exit"
            className="fixed inset-0 z-[55] bg-black"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] via-transparent to-transparent" />

            {/* Close button */}
            <button
              onClick={closeMenu}
              className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center z-10"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <nav className="relative h-full flex flex-col justify-center shorter:justify-start px-6 sm:px-12 md:px-20 overflow-y-auto pt-24 pb-24 short:pt-20 short:pb-12 tiny:pt-16 tiny:pb-8">
              {mobileNavSections.map((section, si) => (
                <m.div
                  key={section.title}
                  custom={si}
                  variants={linkSlide}
                  initial="initial"
                  animate="enter"
                  exit="exit"
                  className="border-b border-white/10 py-3 sm:py-4"
                >
                  {section.links.length === 0 ? (
                    /* Direct link — no dropdown (e.g. Blog) */
                    <Link
                      href={section.href}
                      onClick={closeMenu}
                      className="flex items-center group w-full text-left"
                    >
                      <span className="text-2xl sm:text-3xl md:text-4xl font-[family-name:var(--font-display)] text-white/80 group-hover:text-white transition-colors duration-500">
                        {section.title}
                      </span>
                    </Link>
                  ) : (
                    <button
                      onClick={() => toggleSection(section.title)}
                      className="flex items-center justify-between group w-full text-left"
                    >
                      <span className="text-2xl sm:text-3xl md:text-4xl font-[family-name:var(--font-display)] text-white/80 group-hover:text-white transition-colors duration-500">
                        {section.title}
                      </span>
                      <svg
                        className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 ${
                          mobileSection === section.title
                            ? "rotate-180 text-white/80"
                            : "text-white/50"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  )}

                  <AnimatePresence>
                    {mobileSection === section.title && (
                      <m.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease }}
                        className="overflow-hidden"
                      >
                        <div className="pt-3 pb-1 pl-4 space-y-2">
                          {section.links.map((link) => (
                            <Link
                              key={link.label}
                              href={link.href}
                              onClick={closeMenu}
                              className="block text-base sm:text-lg text-white/40 hover:text-white/80 transition-colors duration-300 py-1"
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </m.div>
                    )}
                  </AnimatePresence>
                </m.div>
              ))}

              {/* Sign In */}
              <m.div
                custom={mobileNavSections.length}
                variants={linkSlide}
                initial="initial"
                animate="enter"
                exit="exit"
                className="border-b border-white/10 py-3 sm:py-4"
              >
                <a
                  href="https://platform.hudson-labs.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between group"
                >
                  <span className="text-2xl sm:text-3xl md:text-4xl font-[family-name:var(--font-display)] text-white/80 group-hover:text-white transition-colors duration-500">
                    Sign In
                  </span>
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-white/50 group-hover:text-white/80 group-hover:-rotate-45 transition-all duration-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </a>
              </m.div>

              {/* CTA */}
              <m.div
                custom={mobileNavSections.length + 1}
                variants={linkSlide}
                initial="initial"
                animate="enter"
                exit="exit"
                className="mt-8 sm:mt-12"
              >
                <button
                  onClick={closeAndTrial}
                  className="inline-flex items-center gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-white text-black rounded-md text-base sm:text-lg hover:bg-neutral-200 transition-colors"
                >
                  Start Free Trial
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                </button>
              </m.div>

              {/* Bottom info */}
              <m.div
                custom={mobileNavSections.length + 2}
                variants={linkSlide}
                initial="initial"
                animate="enter"
                exit="exit"
                className="absolute bottom-8 sm:bottom-12 left-6 sm:left-12 md:left-20 shorter:static shorter:mt-10 shorter:left-0 flex gap-6 sm:gap-8"
              >
                <a
                  href="https://www.linkedin.com/company/hudson-labs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-white/30 hover:text-white/60 transition-colors tracking-wider uppercase"
                >
                  LinkedIn
                </a>
                <a
                  href="https://x.com/HudsonLabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-white/30 hover:text-white/60 transition-colors tracking-wider uppercase"
                >
                  Twitter
                </a>
              </m.div>
            </nav>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
